import React, { ReactNode } from 'react';
import { Input } from 'formik-antd';
import { FormikProps } from 'formik';
import * as Yup from 'yup';
import { Col, Row } from 'antd';
import { isEqual } from 'lodash';

import { isComponentsEqual } from 'utils/reactUtils';
import FormItem from 'components/formik/FormItem';
import useSupervisorDependentField from './hooks/useSupervisorDependentField';
import { CustomerInfoFormValues } from 'components/modals/AssignmentDataModal/CustomerInfo';
import { getFormItemName } from 'utils/fieldArrayUtils';

import style from './TimesheetApproverSection.module.less';

export interface TimesheetApproverFormValues {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

export const timesheetApproverValidationSchema = {
  firstName: Yup.string()
    .trim()
    .max(120, 'First name should be 120 characters maximum'),
  lastName: Yup.string()
    .trim()
    .max(120, 'Last name should be 120 characters maximum'),
  email: Yup.string()
    .email('Email format is invalid')
    .max(120, 'Email should be 120 characters maximum'),
  phone: Yup.string()
    .trim()
    .max(120, 'Phone number should be 120 characters maximum'),
};

type Props = {
  formik: FormikProps<CustomerInfoFormValues>;
  index: number;
  formNamePrefix?: string;
  actionButtons?: ReactNode;
  disabled?: boolean;
  isFirstTimesheetApprover: boolean;
};

export default React.memo(
  (props: Props) => {
    const {
      index,
      actionButtons = null,
      formNamePrefix,
      formik,
      disabled,
    } = props;

    useSupervisorDependentField({
      formik,
      originalFieldName: 'firstName',
    });
    useSupervisorDependentField({
      formik,
      originalFieldName: 'lastName',
    });
    useSupervisorDependentField({
      formik,
      originalFieldName: 'email',
    });
    useSupervisorDependentField({
      formik,
      originalFieldName: 'phone',
    });

    const firstNameFormItem = getFormItemName({
      name: 'firstName',
      index,
      prefix: formNamePrefix,
    });
    const lastNameFormItem = getFormItemName({
      name: 'lastName',
      index,
      prefix: formNamePrefix,
    });
    const phoneFormItem = getFormItemName({
      name: 'phone',
      index,
      prefix: formNamePrefix,
    });
    const emailFormItem = getFormItemName({
      name: 'email',
      index,
      prefix: formNamePrefix,
    });

    return (
      <>
        <Col xs={24} md={12}>
          <Row gutter={20}>
            <Col xs={24} md={12}>
              <FormItem name={firstNameFormItem} label="First name *">
                <Input
                  name={firstNameFormItem}
                  disabled={disabled}
                  placeholder="Enter first name"
                  autoComplete="off"
                />
              </FormItem>
            </Col>

            <Col xs={24} md={12}>
              <FormItem name={lastNameFormItem} label="Last name *">
                <Input
                  name={lastNameFormItem}
                  disabled={disabled}
                  placeholder="Enter last name"
                  autoComplete="off"
                />
              </FormItem>
            </Col>
          </Row>
        </Col>

        <Col xs={24} md={12}>
          <Row gutter={20}>
            <Col xs={24} md={12}>
              <FormItem name={emailFormItem} label="Email *">
                <Input
                  name={emailFormItem}
                  disabled={disabled}
                  placeholder="Enter email"
                  autoComplete="off"
                />
              </FormItem>
            </Col>

            <Col xs={24} md={12}>
              <FormItem label="Phone number *" name={phoneFormItem}>
                <Input
                  name={phoneFormItem}
                  disabled={disabled}
                  placeholder="Enter phone number"
                  autoComplete="off"
                />
              </FormItem>
            </Col>
          </Row>
        </Col>

        {actionButtons && (
          <Col xs={24}>
            <div className={style.action}>{actionButtons}</div>
          </Col>
        )}
      </>
    );
  },
  (prevProps: Props, nextProps: Props) => {
    const arePropsEqual =
      prevProps.index === nextProps.index &&
      prevProps.disabled === nextProps.disabled &&
      isComponentsEqual(prevProps.actionButtons, nextProps.actionButtons);

    // First timesheet approver input values are based on supervisor info
    if (nextProps.isFirstTimesheetApprover) {
      return (
        arePropsEqual &&
        isEqual(
          prevProps.formik.values.supervisorInfo,
          nextProps.formik.values.supervisorInfo,
        ) &&
        isEqual(
          prevProps.formik.touched.supervisorInfo,
          nextProps.formik.touched.supervisorInfo,
        )
      );
    }

    return arePropsEqual;
  },
);
